import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionWizard } from './SubscriptionWizardContext';
import SubscriptionWizardLayout from './SubscriptionWizardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Building, 
  GitBranch, 
  Users, 
  ArrowRight, 
  CheckCircle2,
  FileText,
  Briefcase
} from 'lucide-react';
import { formatCurrency } from '@/utils/format-currency';

const Step1cSubscriptionType = () => {
  const navigate = useNavigate();
  const { state, setSubscriptionType } = useSubscriptionWizard();
  const [shouldNavigate, setShouldNavigate] = useState(false);

  useEffect(() => {
    if (!state.selectedCustomer) {
      navigate('../step1', { relative: 'path' });
      return;
    }
  }, [state.selectedCustomer, navigate]);

  // Navigate after subscription type is set and we want to navigate
  useEffect(() => {
    if (state.subscriptionType && shouldNavigate) {
      // For split subscriptions, go to step2 (child customers selection)
      // For simple subscriptions, go directly to step3 (contacts)
      if (state.subscriptionType === 'split') {
        navigate('../step2', { relative: 'path' });
      } else {
        navigate('../step3', { relative: 'path' });
      }
    }
  }, [state.subscriptionType, shouldNavigate, navigate]);

  const handleTypeSelection = (type: 'simple' | 'split') => {
    setSubscriptionType(type);
    setShouldNavigate(true);
  };

  return (
    <SubscriptionWizardLayout
      currentStep={2}
      totalSteps={6}
      title="Tipo de Assinatura"
      description="Escolha como você deseja estruturar esta assinatura"
      onNext={() => {
        // Navigation handled by useEffect when subscriptionType changes
      }}
      onPrevious={() => navigate('../step1b', { relative: 'path' })}
      isNextDisabled={!state.subscriptionType}
      hideNextButton={true} // We'll handle navigation in card clicks
    >
      <div className="space-y-6">
        {/* Customer and Opportunity Summary */}
        <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-sm font-extrabold text-blue-800 flex items-center">
                <Building className="h-4 w-4 mr-2" />
                Cliente Principal
              </div>
              <div className="font-extrabold mt-1 text-blue-900">
                {state.selectedCustomer?.name}
              </div>
            </div>
            {state.selectedOpportunity && (
              <div className="text-right">
                <div className="text-sm font-extrabold text-blue-800 flex items-center justify-end">
                  <Briefcase className="h-4 w-4 mr-2" />
                  Oportunidade
                </div>
                <div className="font-extrabold mt-1 text-blue-900">
                  {state.selectedOpportunity.dealname}
                </div>
                {state.selectedOpportunity.amount && (
                  <div className="text-sm text-blue-700">
                    {formatCurrency(state.selectedOpportunity.amount, 'BRL')}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Subscription Type Options */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Simple Subscription */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg border-2 ${
              state.subscriptionType === 'simple' 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => handleTypeSelection('simple')}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Users className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Assinatura Simples</CardTitle>
                    <CardDescription>Uma assinatura unificada</CardDescription>
                  </div>
                </div>
                {state.subscriptionType === 'simple' && (
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Crie uma assinatura única que inclui o cliente principal e 
                  todos os participantes em uma única estrutura.
                </p>
                
                <div className="space-y-2">
                  <div className="flex items-center text-sm">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mr-2" />
                    <span>Estrutura mais simples</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mr-2" />
                    <span>Faturamento unificado</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mr-2" />
                    <span>Gestão centralizada</span>
                  </div>
                </div>

                <Badge variant="secondary" className="w-fit">
                  Recomendado para cenários básicos
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Split Subscription */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg border-2 ${
              state.subscriptionType === 'split' 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => handleTypeSelection('split')}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <GitBranch className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Assinatura com Split</CardTitle>
                    <CardDescription>Múltiplas assinaturas derivadas</CardDescription>
                  </div>
                </div>
                {state.subscriptionType === 'split' && (
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Crie uma assinatura principal e depois divida os planos 
                  em múltiplas assinaturas específicas para diferentes clientes.
                </p>
                
                <div className="space-y-2">
                  <div className="flex items-center text-sm">
                    <CheckCircle2 className="h-4 w-4 text-purple-500 mr-2" />
                    <span>Assinatura principal + filhas</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <CheckCircle2 className="h-4 w-4 text-purple-500 mr-2" />
                    <span>Divisão customizada de planos</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <CheckCircle2 className="h-4 w-4 text-purple-500 mr-2" />
                    <span>Faturamento segregado</span>
                  </div>
                </div>

                <Badge variant="secondary" className="w-fit">
                  Para cenários complexos
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Helper Info */}
        <div className="bg-gray-50 p-4 rounded-md">
          <div className="flex items-start space-x-3">
            <FileText className="h-5 w-5 text-gray-600 mt-0.5" />
            <div>
              <h4 className="font-extrabold text-gray-900">Quando usar cada tipo?</h4>
              <div className="mt-2 text-sm text-gray-600 space-y-1">
                <p><strong>Simples:</strong> Quando todos os participantes compartilham os mesmos planos e faturamento.</p>
                <p><strong>Split:</strong> Quando você precisa de faturamento separado ou distribuição específica de planos entre diferentes clientes.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Selected Type Preview */}
        {state.subscriptionType && (
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <span>Tipo selecionado:</span>
              <Badge variant="default">
                {state.subscriptionType === 'simple' ? 'Assinatura Simples' : 'Assinatura com Split'}
              </Badge>
              <ArrowRight className="h-4 w-4" />
              <span>Próximo: Configurar participantes</span>
            </div>
          </div>
        )}
      </div>
    </SubscriptionWizardLayout>
  );
};

export default Step1cSubscriptionType;